import { AxiosError } from 'axios';
import React from 'react';
import { Link } from 'react-aria-components';
import { useForm } from 'react-hook-form';

import { mapPaidBreakExceptionToPaidBreakExceptionFormValue } from '#/shared/adapters/mapPaidBreakExceptionToPaidBreakExceptionFormValue';
import { BackActionButton } from '#/shared/components/BackActionButton';
import { Banner } from '#/shared/components/Banner';
import { Card } from '#/shared/components/Card';
import { FixedButton } from '#/shared/components/FixedButton';
import { FormControlMultiTextInput, FormControlTextArea, FormControlTextInput } from '#/shared/components/FormControl';
import { MaterialIcon } from '#/shared/components/MaterialIcon';
import { appConfig } from '#/shared/config/AppConfig';
import { useAnalytics } from '#/shared/hooks/useAnalytics';
import { useFeatureFlag } from '#/shared/hooks/useFeatureFlag';
import { useMonitoring } from '#/shared/hooks/useMonitoring';
import { usePageStateStore } from '#/shared/stores/pageStateStore';
import { useTimesheetActionViewStore } from '#/shared/stores/timesheetActionViewStore';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';
import { validateEmail, validateRequiredField } from '#/shared/utils/formHelper';

import { mapTimesheetVerificationFormValuesToVerifyTimesheetApiData } from '../adaptors/mapTimesheetVerificationFormValuesToVerifyTimesheetApiData';
import { useVerifyTimesheetMutation } from '../hooks/useVerifyTimesheetMutation';
import { useVerifyFormStore } from '../stores/verifyFormStore';
import { type TimesheetVerificationFormValues } from '../types/TimesheetVerificationFormValues';
import { type VerifyTimesheetApiErrorResponseData } from '../types/VerifyTimesheetApi';

export function VerifyRequestForm() {
  const { noticeError } = useMonitoring();
  const { track } = useAnalytics();
  const { isEnabled: paidBreakExceptionIsEnabled } = useFeatureFlag('PPL-2119-paid-break-exception');
  const setView = useTimesheetActionViewStore(state => state.setView);
  const setPageState = usePageStateStore(state => state.setPageState);
  const verifyFormData = useVerifyFormStore(state => state.formData);
  const setVerifyFormData = useVerifyFormStore(state => state.setFormData);

  const { handleSubmit, control, register, getValues, setValue } = useForm<TimesheetVerificationFormValues>({
    defaultValues: {
      approverName: verifyFormData?.approverName ?? '',
      approverEmail: verifyFormData?.approverEmail ?? '',
      verifierName: verifyFormData?.verifierName ?? '',
      verifierEmails: verifyFormData?.verifierEmails ?? [{ email: '' }],
      verifierRole: verifyFormData?.verifierRole ?? '',
      verifierDepartment: verifyFormData?.verifierDepartment ?? '',
      verifierNotes: verifyFormData?.verifierNotes ?? '',
      paidBreakExceptions: verifyFormData?.paidBreakExceptions ?? [],
    },
    mode: 'onSubmit',
  });

  const verifyTimesheet = useVerifyTimesheetMutation();
  const [isSubmissionError, setIsSubmissionError] = React.useState(false);
  const [isSubmissionPending, setIsSubmissionPending] = React.useState(false);

  const timesheetId = useTimesheetStore(state => state.timesheet?.timesheetId ?? '');

  React.useEffect(() => {
    // Save form data to store on unmount
    return () => {
      setVerifyFormData(getValues());
    };
  }, [getValues, setVerifyFormData]);

  const handleBackToActions = () => {
    track('Online approval web - Press Back to timesheet actions link', {
      Timesheet_ID: timesheetId,
      Source_Screen: 'Verify timesheet',
    });

    setView('actions');
  };

  const onSubmit = async (formData: TimesheetVerificationFormValues) => {
    setIsSubmissionError(false);
    setIsSubmissionPending(true);

    // Transform store exceptions to form values for submission and update form state
    if (paidBreakExceptionIsEnabled) {
      const paidBreakExceptions = mapPaidBreakExceptionToPaidBreakExceptionFormValue();
      formData.paidBreakExceptions = paidBreakExceptions;
      setValue('paidBreakExceptions', paidBreakExceptions);
    }

    const hasVerifierEmail = formData.verifierEmails?.some(value => value.email.trim() !== '') ?? false;

    try {
      const data = mapTimesheetVerificationFormValuesToVerifyTimesheetApiData({
        formValues: formData,
        paidBreakExceptionIsEnabled,
      });

      await verifyTimesheet.mutateAsync({ data });

      track('Online approval web - Verified success', {
        Timesheet_ID: timesheetId,
        Has_Verifier_Email: hasVerifierEmail,
      });

      setIsSubmissionPending(false);
      setPageState('verification-success');
    } catch (error) {
      setIsSubmissionError(true);
      setIsSubmissionPending(false);
      track('Online approval web - Verified failure', {
        Timesheet_ID: timesheetId,
        Has_Verifier_Email: hasVerifierEmail,
      });
      const expectedError = error as AxiosError<VerifyTimesheetApiErrorResponseData>;
      noticeError(new Error('Timesheet online verification has failed'), {
        originalMessage: expectedError?.message,
        originalData: expectedError?.response?.data,
        originalError: expectedError,
        timesheetId,
      });
    }
  };

  return (
    <Card className="max-tablet-portrait:py-32 max-tablet-portrait:pl-20 max-tablet-portrait:pr-20 max-tablet-portrait:border-0 max-tablet-portrait:rounded-none max-tablet-portrait:border-y-1 max-tablet-portrait:bg-primary-surface-l1">
      <BackActionButton onPress={handleBackToActions}>Back to timesheet actions</BackActionButton>

      <form className="gap-24 flex flex-col" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <h2 className="text-24 font-600 mb-8">Verify and send timesheet</h2>
          <p>For timesheets that must be reconciled against the roster before approval.</p>
        </div>

        <Banner
          variant="warning"
          title="Timesheet requires approval after verification"
          supportingText="Verifying a timesheet does not approve it. If no verification is needed, approve it instead."
          action={{
            label: 'Approve timesheet instead',
            onPress: () => setView('approve'),
          }}
        />

        <div className="gap-16 flex flex-col">
          <h3 className="text-20 text-left font-600">Verified by</h3>
          <div className="gap-16 flex flex-col mb-16">
            <FormControlTextInput
              label="Full name"
              placeholder="Enter text"
              name={'verifierName'}
              type="text"
              control={control}
              rules={{
                validate: {
                  required: validateRequiredField,
                },
              }}
              errorLeadingIconName={'error'}
            />
            <FormControlTextInput
              label="Position"
              placeholder="Enter text"
              name={'verifierRole'}
              type="text"
              control={control}
              rules={{
                validate: {
                  required: validateRequiredField,
                },
              }}
              errorLeadingIconName={'error'}
            />
            <FormControlTextInput
              label="Department (optional)"
              placeholder="Enter text"
              name={'verifierDepartment'}
              type="text"
              control={control}
            />
            <FormControlTextArea
              label="Verifier notes (optional)"
              description="Included on timesheet and visible to the approver, doctor, and Medrecruit staff."
              descriptionPosition="above"
              placeholder="Enter text"
              name={'verifierNotes'}
              control={control}
              maxLength={255}
              rules={{
                maxLength: { value: 255, message: 'Maximum allowed characters is 255' },
              }}
              errorLeadingIconName={'error'}
            />
          </div>
        </div>

        <div className="gap-16 flex flex-col">
          <h3 className="text-20 text-left font-600">Send to approver</h3>
          <FormControlTextInput
            label="Approver's full name"
            placeholder="Enter text"
            name={'approverName'}
            type="text"
            control={control}
            rules={{
              validate: {
                required: validateRequiredField,
              },
            }}
            errorLeadingIconName={'error'}
          />
          <FormControlTextInput
            label="Approver's email address"
            description="By forwarding, you confirm that you are authorised to share this email address."
            descriptionPosition="above"
            placeholder="Enter email"
            name={'approverEmail'}
            type="text"
            control={control}
            rules={{
              validate: {
                email: validateEmail,
                required: validateRequiredField,
              },
            }}
            errorLeadingIconName={'error'}
          />
          <FormControlMultiTextInput
            name="verifierEmails"
            control={control}
            register={register}
            validate={validateEmail}
            label="Your email address (optional)"
            description="If you'd like to receive a copy of the approved timesheet."
            placeholder="Enter email"
            addFieldButtonLabel="Add another email address"
            type="text"
            fieldKey="email"
            minFields={1}
            maxFields={5}
          />
        </div>

        {isSubmissionError && (
          <div className="text-14 text-error text-left flex gap-4">
            <MaterialIcon iconName={'error'} size={'sm'} />
            <p>
              Sorry, unable to process timesheet verification—the timesheet has not been sent to the approver. Please
              try again later or contact{' '}
              <Link
                href={`mailto:${appConfig.feedback.email}`}
                className="text-primary hover:text-primary-shade focus:text-primary-shade hover:underline"
              >
                {appConfig.feedback.email}
              </Link>{' '}
              if the issue persists.
            </p>
          </div>
        )}

        <div className="pt-8">
          <FixedButton type="submit" disabled={isSubmissionPending} isLoading={isSubmissionPending}>
            Verify and send timesheet
          </FixedButton>
        </div>
      </form>
    </Card>
  );
}
